import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AttendanceSummaryResponse, CircleAttendanceResponse } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, PageTitle, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { useMe } from "../lib/session";

const iso = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Attendance (docs/09 §2): the day's overview per circle, and a circle's sheet to review or correct.
 * Corrections go through the same endpoint as the teacher's app (absence policy B9, audit log).
 */
export function AttendancePage() {
  const { data: me } = useMe();
  const today = iso(new Date());
  const [date, setDate] = useState(today);
  const [circleId, setCircleId] = useState<string | null>(null);
  const summary = useQuery({
    queryKey: ["attendance-summary", me?.activeRole, date],
    queryFn: () => api<AttendanceSummaryResponse>(`/v1/attendance/summary?date=${date}`),
  });
  const totals = useMemo(
    () =>
      (summary.data?.circles ?? []).reduce(
        (t, c) => ({
          students: t.students + c.students,
          present: t.present + c.present,
          absent: t.absent + c.absent,
          unrecorded: t.unrecorded + c.unrecorded,
        }),
        { students: 0, present: 0, absent: 0, unrecorded: 0 },
      ),
    [summary.data],
  );

  return (
    <>
      <PageTitle
        title="الحضور"
        sub="حضور اليوم لكل حلقة ضمن نطاقك. اختر حلقة لعرض الطلاب أو تصحيح التسجيل."
      />
      <section className="card">
        <div className="form section">
          <div className="form-row">
            <Field id="at-date" label="اليوم">
              <input
                id="at-date"
                type="date"
                dir="ltr"
                value={date}
                max={today}
                onChange={(e) => {
                  setDate(e.target.value);
                  setCircleId(null);
                }}
              />
            </Field>
          </div>
        </div>
      </section>

      <div className="stats">
        <div className="stat">
          <span className="k">الطلاب</span>
          <span className="v num">{totals.students}</span>
        </div>
        <div className="stat">
          <span className="k">حاضر</span>
          <span className="v num">{totals.present}</span>
        </div>
        <div className="stat">
          <span className="k">غائب</span>
          <span className="v num">{totals.absent}</span>
        </div>
        <div className={totals.unrecorded ? "stat attention" : "stat"}>
          <span className="k">لم يُسجل</span>
          <span className="v num">{totals.unrecorded}</span>
        </div>
      </div>

      <section className="card">
        {summary.isPending ? (
          <Loading />
        ) : summary.isError ? (
          <Empty title="تعذر التحميل">{errorText(summary.error)}</Empty>
        ) : summary.data.circles.length === 0 ? (
          <Empty title="لا حلقات ضمن نطاقك" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الحلقة</th>
                  <th>المجمع</th>
                  <th>الطلاب</th>
                  <th>حاضر</th>
                  <th>غائب</th>
                  <th>لم يُسجل</th>
                </tr>
              </thead>
              <tbody>
                {summary.data.circles.map((c) => (
                  <tr
                    key={c.circle.id}
                    className="clickable"
                    aria-selected={circleId === c.circle.id}
                    onClick={() => setCircleId(c.circle.id)}
                  >
                    <td>
                      <button className="link" onClick={() => setCircleId(c.circle.id)}>
                        {c.circle.name}
                      </button>
                    </td>
                    <td>{c.branchName}</td>
                    <td className="num">{c.students}</td>
                    <td className="num">{c.present}</td>
                    <td className="num">{c.absent}</td>
                    <td className="num">
                      {c.unrecorded ? <Badge tone="warning">{c.unrecorded}</Badge> : c.unrecorded}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {circleId && (
        <CircleSheet
          key={`${circleId}:${date}`}
          circleId={circleId}
          name={summary.data?.circles.find((c) => c.circle.id === circleId)?.circle.name ?? ""}
          date={date}
        />
      )}
    </>
  );
}

function CircleSheet({ circleId, name, date }: { circleId: string; name: string; date: string }) {
  const qc = useQueryClient();
  const sheet = useQuery({
    queryKey: ["attendance", circleId, date],
    queryFn: () => api<CircleAttendanceResponse>(`/v1/circles/${circleId}/attendance?date=${date}`),
  });
  const [chosen, setChosen] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!sheet.data) return;
    setChosen(
      Object.fromEntries(
        sheet.data.students.flatMap((s) => (s.record ? [[s.enrollmentId, s.record.statusId]] : [])),
      ),
    );
  }, [sheet.data]);
  const changed = (sheet.data?.students ?? []).filter(
    (s) => chosen[s.enrollmentId] && chosen[s.enrollmentId] !== s.record?.statusId,
  );
  const { busy, error, run } = useSubmit();
  const [saved, setSaved] = useState(false);

  function save() {
    setSaved(false);
    void run(async () => {
      await send("PUT", `/v1/circles/${circleId}/attendance`, {
        date,
        entries: changed.map((s) => ({ enrollmentId: s.enrollmentId, statusId: chosen[s.enrollmentId]! })),
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["attendance", circleId, date] }),
        qc.invalidateQueries({ queryKey: ["attendance-summary"] }),
      ]);
      setSaved(true);
    }, errorText);
  }

  return (
    <section className="card" aria-labelledby="sheet-h">
      <div className="card-h">
        <h2 id="sheet-h">
          {name} · <span className="num">{date}</span>
        </h2>
      </div>
      {sheet.isPending ? (
        <Loading />
      ) : sheet.isError ? (
        <Empty title="تعذر التحميل">{errorText(sheet.error)}</Empty>
      ) : sheet.data.students.length === 0 ? (
        <Empty title="لا طلاب في هذه الحلقة" />
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الطالب</th>
                  <th>الحالة</th>
                  <th>المصدر</th>
                </tr>
              </thead>
              <tbody>
                {sheet.data.students.map((s) => (
                  <tr key={s.enrollmentId}>
                    <td>{s.student.name}</td>
                    <td>
                      <select
                        aria-label={`حالة ${s.student.name}`}
                        value={chosen[s.enrollmentId] ?? ""}
                        onChange={(e) => setChosen((c) => ({ ...c, [s.enrollmentId]: e.target.value }))}
                      >
                        <option value="" disabled>
                          لم يُسجل
                        </option>
                        {sheet.data.statuses.map((st) => (
                          <option key={st.id} value={st.id}>
                            {st.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="muted small">
                      {s.record ? (s.record.source === "session" ? "تلقائي من التسميع" : "المعلم") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="form section">
            <ErrorNote error={error} />
            {saved && !changed.length && <p className="muted small">حُفظ التعديل وسُجّل في سجل التدقيق.</p>}
            <div className="form-actions">
              <button className="btn btn-primary" disabled={busy || changed.length === 0} onClick={save}>
                {busy ? "جارٍ الحفظ…" : `حفظ التعديلات (${changed.length})`}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
